'use client'

import { useMemo } from 'react'

import type { CategoryNode, TutorialSection } from '@/lib/github/types'
import { ACADEMY_ROUTES } from '@/shared/docs-shared.const'
import { QUICK_START_LAYOUT_SHELL_TEST_IDS } from '@/shared/docs-test-ids.const'
import { useCurrentSectionPath, useSectionBreadcrumbs } from '@/shared/hooks'

import { AcademyLayout } from '../AcademyLayout'

import { QuickStartLanguageContext } from './context/quickStartLanguageContext'
import { useQuickStartLanguage } from './hooks/useQuickStartLanguage'
import { collectAvailableLanguages } from './utils/collectAvailableLanguages'

interface QuickStartLayoutShellProps {
  sections: TutorialSection[]
  categoryTree: CategoryNode[]
  rootArticles: TutorialSection[]
  children: React.ReactNode
}

export function QuickStartLayoutShell({ sections, categoryTree, rootArticles, children }: QuickStartLayoutShellProps) {
  const currentSectionPath = useCurrentSectionPath({
    routePrefix: ACADEMY_ROUTES.quickStart,
    sections,
  })

  const breadcrumbs = useSectionBreadcrumbs(sections, currentSectionPath, 'Quick Start')

  const availableLanguages = useMemo(() => collectAvailableLanguages(sections), [sections])
  const { selectedLanguage, setSelectedLanguage, selectedOs, setSelectedOs } = useQuickStartLanguage(availableLanguages)

  const languageContextValue = useMemo(
    () => ({ sections, selectedLanguage, setSelectedLanguage, availableLanguages, selectedOs, setSelectedOs }),
    [sections, selectedLanguage, setSelectedLanguage, availableLanguages, selectedOs, setSelectedOs]
  )

  return (
    <QuickStartLanguageContext data-testid={QUICK_START_LAYOUT_SHELL_TEST_IDS.root} value={languageContextValue}>
      <AcademyLayout
        tutorialSections={sections}
        currentTutorialPath={currentSectionPath}
        breadcrumbs={breadcrumbs}
        categoryTree={categoryTree}
        rootArticles={rootArticles}
      >
        {children}
      </AcademyLayout>
    </QuickStartLanguageContext>
  )
}
